import React, { useEffect, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import useDebounce from 'components/hooks/useDebounce';
import useForm from 'components/hooks/useForm';
import useRequestError from 'components/hooks/useRequestError';
import useSidePanel from 'components/hooks/useSidePanel';
import useValidatedStore from 'components/hooks/useValidatedStore';
import useWindowStorage from 'components/hooks/useWindowStorage';

import DestroyModal from 'components/application/DestroyModal';
import Paginator from 'components/application/Paginator';
import ResourceBlankNotice from 'components/application/ResourceBlankNotice';
import SuccessToast from 'components/application/SuccessToast';

import { useBreadBoard } from 'components/contexts/Toaster';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import SubcontractorsBar from 'components/subcontractors/SubcontractorsBar';
import SubcontractorsSidePanel from 'components/subcontractors/SubcontractorsSidePanel';
import SubcontractorsTable from 'components/subcontractors/SubcontractorsTable';

const defaultSubcontractor = {
  id: '',
  name: '',
  isDeleteProhibited: false,
};

const initialTabStore = {
  currentSearch: '',
  page: 1,
  currentResourceId: '',
  currentResourceName: '',
  sidePanelIsOpen: false,
};

const tabSchema = z.object({
  currentResourceId: z.string().nullable(),
  currentResourceName: z.string(),
  sidePanelIsOpen: z.boolean(),
  page: z.number().or(z.null()),
  currentSearch: z.string(),
});

export default function SubcontractorsTab() {
  const breadBoard = useBreadBoard();

  const { hasPersonnelEditableAccess } = useTrainingRegisterResources();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'subcontractors.SubcontractorsTab',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const [getStore, setStore] = useWindowStorage(
    'trainingRegister|subcontractors',
    { store: window.sessionStorage },
  );

  const tabStore = useValidatedStore({
    getStore,
    initialStore: initialTabStore,
    schema: tabSchema,
  });

  const initialCurrentSearch = { subcontractorsSearch: tabStore.currentSearch };

  const initialDefaultSubcontractor = {
    ...defaultSubcontractor,
    id: tabStore.currentResourceId,
    name: tabStore.currentResourceName,
  };

  const isInitialSidePanelOpen = tabStore.sidePanelIsOpen || false;

  const [subcontractors, setSubcontractors] = useState({
    loaded: false,
    collection: [],
  });

  const [metaData, setMetaData] = useState({
    currentPage: null,
    totalPages: null,
    scopedCount: 0,
    totalCount: 0,
  });

  const [
    requestError,
    submitDisabled,
    removeErrorStyling,
    resetRequestError,
    handleRequestError,
  ] = useRequestError();

  const [currentSearch, setCurrentSearch, handleSearchInputChange] =
    useForm(initialCurrentSearch);

  const [debouncedCurrentSearch, _resetDebouncedCurrentSearch] = useDebounce(
    currentSearch,
    250,
  );

  const [
    currentSubcontractor,
    setCurrentSubcontractor,
    handleSubcontractorInputChange,
  ] = useForm(initialDefaultSubcontractor);

  const [
    sidePanelIsOpen,
    setSidePanelIsOpen,
    openSidePanel,
    closeSidePanel,
    resetSidePanelContext,
    sidePanelContext,
    setSidePanelContext,
  ] = useSidePanel(isInitialSidePanelOpen, 'show');

  const [isDestroyModalOpen, setIsDestroyModalOpen] = useState(false);

  useEffect(() => {
    fetchSubcontractors(tabStore.page);
  }, []);

  useEffect(() => {
    if (subcontractors.loaded && debouncedCurrentSearch) {
      fetchSubcontractors();
    }
  }, [debouncedCurrentSearch]);

  const fetchSubcontractors = (page = 1) => {
    axios
      .get('/subcontractors', {
        params: { search: currentSearch.subcontractorsSearch, page },
      })
      .then((response) => {
        setSubcontractors({ loaded: true, collection: response.data.data });
        setMetaData(response.data.meta);
        resetSidePanelContext();
      })
      .catch((_error) => breadBoard.addInedibleToast());
  };

  const createSubcontractor = () => {
    axios
      .post('/subcontractors', {
        subcontractor: { name: currentSubcontractor.name },
      })
      .then((response) => {
        closeSidePanel();
        resetRequestError();

        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey='shared.success.added'
                ns='common'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{ name: response.data.data.attributes.name }}
              />
            }
          />,
        );

        fetchSubcontractors();
      })
      .catch((error) => {
        if (error.response.status === 422) {
          handleRequestError(error);
        } else {
          breadBoard.addInedibleToast();
          closeSidePanel();
        }
      });
  };

  const updateSubcontractor = () => {
    axios
      .patch(`/subcontractors/${currentSubcontractor.id}`, {
        subcontractor: { name: currentSubcontractor.name },
      })
      .then((response) => {
        closeSidePanel();
        resetRequestError();
        fetchSubcontractors(metaData.currentPage);

        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey='shared.success.updated'
                ns='common'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{ name: response.data.data.attributes.name }}
              />
            }
          />,
        );
      })
      .catch(handleRequestError);
  };

  const destroySubcontractor = () => {
    axios
      .delete(`/subcontractors/${currentSubcontractor.id}`)
      .then(() => {
        closeSidePanel();
        fetchSubcontractors(metaData.currentPage);

        breadBoard.addToast(
          <SuccessToast
            message={
              currentSubcontractor.archivedPersonnelCount > 0 ?
                <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey='subcontractors.SubcontractorsTab.destroySubcontractor.success'
                  ns='components'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    count: currentSubcontractor.archivedPersonnelCount,
                    name: currentSubcontractor.name,
                  }}
                />
              : <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey='shared.success.deleted'
                  ns='common'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{ name: currentSubcontractor.name }}
                />
            }
          />,
        );
      })
      .catch(() => {
        breadBoard.addInedibleToast();
        closeSidePanel();
      });
  };

  const handleNewSubcontractor = () => {
    setCurrentSubcontractor(defaultSubcontractor);
    setSidePanelContext('new');
    resetRequestError();
    openSidePanel();
  };

  const handleSearchReset = (_event) => {
    setCurrentSearch({ ...currentSearch, subcontractorsSearch: '' });
  };

  const handlePageChange = (event) => {
    fetchSubcontractors(event.currentTarget.getAttribute('data-page'));
  };

  const viewSubcontractor = (subcon) => {
    setCurrentSubcontractor({
      id: subcon.id,
      name: subcon.attributes.name,
      isDeleteProhibited: subcon.meta.isDeleteProhibited,
      archivedPersonnelCount: subcon.meta.archivedPersonnelCount,
    });

    setSidePanelContext('show');
    resetRequestError();
    openSidePanel();
  };

  useEffect(() => {
    setStore({
      currentSearch: currentSearch.subcontractorsSearch,
      page: metaData.currentPage,
      currentResourceId: currentSubcontractor.id,
      currentResourceName: currentSubcontractor.name,
      sidePanelIsOpen,
    });
  }, [
    currentSearch.subcontractorsSearch,
    metaData.currentPage,
    currentSubcontractor.id,
    currentSubcontractor.name,
    sidePanelIsOpen,
  ]);

  return (
    subcontractors.loaded && (
      <>
        {metaData.totalCount > 0 && (
          <SubcontractorsBar
            isActionButtonVisible={hasPersonnelEditableAccess}
            onNewSubcontractor={handleNewSubcontractor}
            onSearchInputChange={handleSearchInputChange}
            onSearchReset={handleSearchReset}
            subcontractorsCount={metaData.totalCount}
            subcontractorsSearch={currentSearch.subcontractorsSearch}
          />
        )}
        {subcontractors.collection.length > 0 ?
          <>
            <SubcontractorsTable
              onRowClick={(_event, subcontractor) => {
                viewSubcontractor(subcontractor);
              }}
              subcontractors={subcontractors.collection}
            />
            {subcontractors.loaded &&
              subcontractors.collection.length > 0 &&
              metaData.totalPages > 1 && (
                <div className='text-center tw-mt-20'>
                  <Paginator
                    currentPage={metaData.currentPage}
                    onClick={handlePageChange}
                    totalPages={metaData.totalPages}
                  />
                </div>
              )}
          </>
        : <ResourceBlankNotice
            addButtonText={i18nTCommon('subcontractors.addNewSubContractor')}
            isAddable={hasPersonnelEditableAccess}
            noResourcesDescription={i18nT(
              `blankNotice.noResourcesDescription.${hasPersonnelEditableAccess ? 'editable' : 'readOnly'}`,
            )}
            noResourcesFoundTitle={i18nT('blankNotice.noResourcesFoundTitle')}
            noResourcesTitle={i18nT(
              `blankNotice.noResourcesTitle.${hasPersonnelEditableAccess ? 'editable' : 'readOnly'}`,
            )}
            onAdd={handleNewSubcontractor}
            totalCount={metaData.totalCount}
          />
        }
        <SubcontractorsSidePanel
          allowShowFooterContents={hasPersonnelEditableAccess}
          closeSidePanel={closeSidePanel}
          createSubcontractor={createSubcontractor}
          currentSubcontractor={currentSubcontractor}
          onDeleteClick={() => setIsDestroyModalOpen(true)}
          onSubcontractorInputChange={handleSubcontractorInputChange}
          removeErrorStyling={removeErrorStyling}
          requestError={requestError}
          setCurrentSubcontractor={setCurrentSubcontractor}
          setSidePanelContext={setSidePanelContext}
          setSidePanelIsOpen={setSidePanelIsOpen}
          sidePanelContext={sidePanelContext}
          sidePanelIsOpen={sidePanelIsOpen}
          submitDisabled={submitDisabled}
          updateSubcontractor={updateSubcontractor}
        />
        {sidePanelContext === 'show' &&
          (currentSubcontractor.archivedPersonnelCount > 0 ?
            <DestroyModal
              additionalContent={
                <p className='tw-mb-0 tw-mt-6'>
                  {i18nT(
                    'destroyModal.companyAndArchivedPersonnel.additionalContent',
                  )}
                </p>
              }
              confirmationText={i18nT(
                'destroyModal.companyAndArchivedPersonnel.confirmationText',
                { count: currentSubcontractor.archivedPersonnelCount },
              )}
              displayText={i18nT(
                'destroyModal.companyAndArchivedPersonnel.displayText',
                { count: currentSubcontractor.archivedPersonnelCount },
              )}
              isOpen={isDestroyModalOpen}
              onClose={() => setIsDestroyModalOpen(false)}
              onDestroy={destroySubcontractor}
            />
          : <DestroyModal
              confirmationText={i18nTCommon(
                'subcontractors.deleteSubContractor',
              )}
              displayText={i18nTCommon('shared.areYouSureYouWantToDelete', {
                interpolation: { escapeValue: false },
                name: currentSubcontractor.name,
              })}
              isOpen={isDestroyModalOpen}
              onClose={() => setIsDestroyModalOpen(false)}
              onDestroy={destroySubcontractor}
            />)}
      </>
    )
  );
}
